import s from './BookingGuide.module.scss';
import { useLanguage } from '../contexts/LanguageContext';
import { getSectionText } from '../data/sectionTexts';
import { bookingGuideText } from '../i18n/bookingGuide';
import InstagramLogo from '../components/InstagramLogo';
import LineLogo from '../components/LineLogo';
import Title from '../Title/Title';
import { INSTAGRAM_URL, LINE_URL } from '../config/socialLinks';
import { trackCtaClick } from '../utils/analytics';

const IgGradient = ({ id }) => (
  <defs>
    <linearGradient id={id} x1="0%" y1="100%" x2="100%" y2="0%">
      <stop offset="0%" stopColor="#ff8a3d" />
      <stop offset="45%" stopColor="#f02cff" />
      <stop offset="100%" stopColor="#6c5cff" />
    </linearGradient>
  </defs>
);

const SendIcon = () => (
  <svg viewBox="0 0 24 24">
    <IgGradient id="igSend" />
    <path d="M22 2L11 13" stroke="url(#igSend)" />
    <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="url(#igSend)" />
  </svg>
);

const MessageIcon = () => (
  <svg viewBox="0 0 24 24">
    <IgGradient id="igMessage" />
    <path
      d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
      stroke="url(#igMessage)"
    />
    <circle cx="9" cy="10" r="1" fill="#f02cff" />
    <circle cx="14" cy="10" r="1" fill="#f02cff" />
    <circle cx="19" cy="10" r="1" fill="#f02cff" />
  </svg>
);

const ChecklistIcon = () => (
  <svg viewBox="0 0 24 24">
    <IgGradient id="igList" />
    <path d="M9 11l3 3L22 4" stroke="url(#igList)" />
    <path
      d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
      stroke="url(#igList)"
    />
  </svg>
);

const CardIcon = ({ line = false }) => (
  <svg viewBox="0 0 24 24">
    {!line && <IgGradient id="igCard" />}
    <rect
      x="2"
      y="5"
      width="20"
      height="14"
      rx="2"
      stroke={line ? '#1ecb4f' : 'url(#igCard)'}
    />
    <path d="M2 10h20" stroke={line ? '#1ecb4f' : 'url(#igCard)'} />
  </svg>
);

const UserAddIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <path d="M20 8v6" />
    <path d="M23 11h-6" />
  </svg>
);

const ChatIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const CalendarCheckIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M8 2v4" />
    <path d="M16 2v4" />
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M3 10h18" />
    <path d="M9 16l2 2 4-4" />
  </svg>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M9 6l6 6-6 6" />
  </svg>
);

const ExternalIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M14 3h7v7" />
    <path d="M10 14L21 3" />
    <path d="M21 14v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h6" />
  </svg>
);

const instagramIcons = [
  <SendIcon />,
  <MessageIcon />,
  <ChecklistIcon />,
  <CardIcon />,
];

const lineIcons = [
  <UserAddIcon />,
  <ChatIcon />,
  <CalendarCheckIcon />,
  <CardIcon line />,
];

const BookingGuide = () => {
  const { lang } = useLanguage();
  const titleText = getSectionText(lang, 'booking');
  const text = bookingGuideText[lang] || bookingGuideText.JP;

  const instagramSteps = text.instagram.steps.map((step, index) => ({
    ...step,
    icon: instagramIcons[index],
  }));

  const lineSteps = text.line.steps.map((step, index) => ({
    ...step,
    icon: lineIcons[index],
  }));

  return (
    <section className={s.root}>
      <Title kicker={titleText.kicker} title={titleText.title} />

      <div className={s.columns}>
        <div className={`${s.column} ${s.instagram}`}>
          <div className={s.channelTitle}>
            <InstagramLogo />
            <h3>{text.instagram.title}</h3>
          </div>

          <div className={s.steps}>
            {instagramSteps.map((step, index) => (
              <div className={s.step} key={step.number}>
                <span className={s.number}>{step.number}</span>
                <div className={s.icon}>{step.icon}</div>
                <h4>{step.title}</h4>
                <p>{step.text}</p>

                {index !== instagramSteps.length - 1 && (
                  <div className={s.arrow}>
                    <ArrowIcon />
                  </div>
                )}
              </div>
            ))}
          </div>

          <a
            className={s.cta}
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noreferrer"
            aria-label={text.instagram.cta}
            onClick={() => trackCtaClick({
              label: 'instagram_booking_guide',
              placement: 'booking_guide',
              destination: INSTAGRAM_URL,
              destinationType: 'external_social',
            })}
          >
            <InstagramLogo />
            {text.instagram.cta}
            <ExternalIcon />
          </a>

          <p className={s.note}>\ {text.instagram.note} /</p>
        </div>

        <div className={s.divider}>
          <span>{text.divider}</span>
        </div>

        <div className={`${s.column} ${s.line}`}>
          <div className={s.channelTitle}>
            <LineLogo />
            <h3>{text.line.title}</h3>
          </div>

          <div className={s.steps}>
            {lineSteps.map((step, index) => (
              <div className={s.step} key={step.number}>
                <span className={s.number}>{step.number}</span>
                <div className={s.icon}>{step.icon}</div>
                <h4>{step.title}</h4>
                <p>{step.text}</p>

                {index !== lineSteps.length - 1 && (
                  <div className={s.arrow}>
                    <ArrowIcon />
                  </div>
                )}
              </div>
            ))}
          </div>

          <a
            className={s.cta}
            href={LINE_URL}
            target="_blank"
            rel="noreferrer"
            aria-label={text.line.cta}
            onClick={() => trackCtaClick({
              label: 'line_booking_guide',
              placement: 'booking_guide',
              destination: LINE_URL,
              destinationType: 'external_booking',
            })}
          >
            <LineLogo />
            {text.line.cta}
            <ExternalIcon />
          </a>

          <p className={s.note}>\ {text.line.note} /</p>
        </div>
      </div>
    </section>
  );
};

export default BookingGuide;
